import { useEffect, useMemo, useState } from "react";
import { adminCreateMenu, adminDeleteMenu, adminFetchMenu, adminUpdateMenu, adminToggleItemAvailability, fetchMenuCategories } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import EmberButton from "@/components/EmberButton";
import { toast } from "sonner";
import { Edit2, Trash2, Plus, Flame, Ban, CheckCircle2 } from "lucide-react";

const EMPTY = { name: "", description: "", price: "", category: "", tags: [], image_url: "", is_special: false, is_active: true, sort_order: 0 };

export default function AdminMenuPage() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const [its, cats] = await Promise.all([adminFetchMenu(), fetchMenuCategories()]);
        setItems(its); setCategories(cats);
    };

    useEffect(() => { load().catch(() => {}); }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => i.name.toLowerCase().includes(q) || (i.description || "").toLowerCase().includes(q));
    }, [items, search]);

    const startCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, category: categories[0]?.slug || "" });
        setOpen(true);
    };
    const startEdit = (it) => {
        setEditing(it);
        setForm({
            name: it.name || "",
            description: it.description || "",
            price: it.price || "",
            category: it.category || "",
            tags: it.tags || [],
            image_url: it.image_url || "",
            is_special: !!it.is_special,
            is_active: it.is_active !== false,
            sort_order: it.sort_order || 0,
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.name || !form.price || !form.category) {
            toast.error("Name, price, and category are required.");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                const updated = await adminUpdateMenu(editing.id, form);
                setItems((p) => p.map((x) => (x.id === editing.id ? updated : x)));
                toast.success("Menu item updated.");
            } else {
                const created = await adminCreateMenu(form);
                setItems((p) => [...p, created]);
                toast.success("Menu item added.");
            }
            setOpen(false);
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        await adminDeleteMenu(id);
        setItems((p) => p.filter((x) => x.id !== id));
        toast.success("Deleted.");
    };

    const toggle86 = async (it) => {
        try {
            const result = await adminToggleItemAvailability(it.id);
            setItems((p) => p.map((x) => x.id === it.id ? { ...x, is_available: result.is_available, available_reset_at: result.available_reset_at } : x));
            toast.success(result.is_available ? `${it.name} is back on the menu.` : `${it.name} 86'd — auto-restores at next open.`);
        } catch (e) {
            toast.error("Toggle failed");
        }
    };

    return (
        <div data-testid="admin-menu-manager">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                    <h1 className="font-display text-3xl tracking-[0.05em] text-white">Menu Manager</h1>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Input placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-black/40 border-white/10 sm:w-64" data-testid="admin-menu-search" />
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button onClick={startCreate} className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase flex items-center gap-1" data-testid="admin-menu-add-button"><Plus size={14}/> Add Item</button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg bg-[#0a0a0b] border border-white/10 text-white" data-testid="admin-menu-form-dialog">
                            <DialogHeader>
                                <DialogTitle>{editing ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="sm:col-span-2">
                                    <Label className="text-white/80">Name</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-menu-form-name" />
                                </div>
                                <div>
                                    <Label className="text-white/80">Price</Label>
                                    <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="$13 or $13/$10" className="mt-1 bg-black/40 border-white/10" data-testid="admin-menu-form-price" />
                                </div>
                                <div>
                                    <Label className="text-white/80">Category</Label>
                                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                        <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="admin-menu-form-category"><SelectValue placeholder="Select" /></SelectTrigger>
                                        <SelectContent>
                                            {categories.map((c) => (
                                                <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-white/80">Description</Label>
                                    <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-menu-form-description" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label className="text-white/80">Image URL</Label>
                                    <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-menu-form-image-url" />
                                </div>
                                <div>
                                    <Label className="text-white/80">Sort order</Label>
                                    <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-menu-form-sort" />
                                </div>
                                <div className="flex items-center gap-3 mt-6">
                                    <div className="flex items-center gap-2">
                                        <Switch checked={form.is_special} onCheckedChange={(v) => setForm({ ...form, is_special: v })} data-testid="admin-menu-form-special" />
                                        <Label className="text-white/80">Special</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="admin-menu-form-active" />
                                        <Label className="text-white/80">Active</Label>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <button className="px-4 py-2 rounded-md border border-white/10 text-white/80" onClick={() => setOpen(false)} data-testid="admin-menu-form-cancel">Cancel</button>
                                <button className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase" onClick={save} disabled={saving} data-testid="admin-menu-form-save">{saving ? "Saving..." : "Save"}</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Card className="surface-card border-0">
                <CardContent className="p-0">
                    <div className="divide-y divide-white/10" data-testid="admin-menu-list">
                        {filtered.length === 0 && (
                            <div className="p-6 text-white/65">No menu items.</div>
                        )}
                        {filtered.map((it) => (
                            <div key={it.id} className="flex items-start justify-between gap-4 p-4 sm:p-5" data-testid="admin-menu-row">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="font-display text-lg tracking-[0.05em] text-white">{it.name}</div>
                                        <Badge variant="outline" className="border-white/15 text-white/65 text-[10px]">{it.category}</Badge>
                                        {it.is_special && <Badge className="bg-orange-400/15 text-orange-300 border-orange-400/30" variant="outline"><Flame size={10} className="mr-1" /> Special</Badge>}
                                        {!it.is_active && <Badge variant="outline" className="border-red-500/30 text-red-300">Hidden</Badge>}
                                        {it.is_available === false && <Badge variant="outline" className="border-red-400/40 text-red-300 bg-red-500/10">86'd</Badge>}
                                    </div>
                                    {it.description && <p className="text-white/65 text-sm mt-1">{it.description}</p>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="text-orange-300 font-display text-lg whitespace-nowrap mr-2">{it.price}</div>
                                    <button
                                        title={it.is_available === false ? "Restore to menu" : "86 this item (auto-restores tomorrow)"}
                                        className={`p-2 rounded-md border hover:bg-white/5 ${it.is_available === false ? 'border-green-500/30 text-green-300' : 'border-yellow-500/20 text-yellow-300'}`}
                                        onClick={() => toggle86(it)}
                                        aria-label={it.is_available === false ? "Restore" : "86 item"}
                                        data-testid="admin-menu-86-button"
                                    >
                                        {it.is_available === false ? <CheckCircle2 size={16}/> : <Ban size={16}/>}
                                    </button>
                                    <button className="p-2 rounded-md border border-white/10 hover:bg-white/5" onClick={() => startEdit(it)} aria-label="Edit" data-testid="admin-menu-edit-button"><Edit2 size={16}/></button>
                                    <button className="p-2 rounded-md border border-red-500/20 text-red-300 hover:bg-red-500/10" onClick={() => remove(it.id)} aria-label="Delete" data-testid="admin-menu-delete-button"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
